import React, {useState, useCallback} from 'react';
import styled from 'styled-components';
import {Image, ImageProps} from '../images';
import {Eyebrow, EyebrowProps} from '../eyebrow/Eyebrow';
import {
    ArrowLeft,
    ArrowRight,
    CarouselArrowLeftAlt,
    CarouselArrowRightAlt,
    CenteredGrid,
    GridContainer,
    Typography,
    VerticalSpacer
} from '../';
import {ImageGalleryLogo, ImageGalleryLogoProps} from './ImageGallery.logo';
import Grid from '@material-ui/core/Grid';
import {ImageGalleryPeak} from './ImageGallery.peak';
import {ImageGalleryTrack} from './ImageGallery.track';
import {AccentColorOptions, useBreakpointDown} from 'epic-ue-theme';
import {useSwipeable} from 'react-swipeable';
import {UeLink, WithCaption, WithCaptionProps} from '../../wraps';
import {useUnrealUIContext} from '../../../shared/context';
import {AnimatePresence, motion} from 'framer-motion';

const Styled = styled.div<{accentColor: AccentColorOptions}>`
    text-align: center;
    overflow: hidden;
    .active-slide-wrap {
        position: relative;
    }
    .track-wrap {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        align-content: center;
        justify-content: center;
        align-items: center;
        ${(p) => p.theme.breakpoints.down('sm')} {
            justify-content: space-between;
        }
        .arrow-wrap {
            position: static;
            svg.arrow {
                font-size: 36px;
                ${(p) => p.theme.breakpoints.down('xs')} {
                    font-size: 28px;
                }
            }
        }
    }
    .peak-slide-wrap {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;

        ${(p) => p.theme.breakpoints.down('sm')} {
            display: none;
        }
    }
    .arrow-wrap {
        position: absolute;
        z-index: 2;
    }
    .ue-link-wrap-span {
        transition: color 0.2s ${(p) => p.theme.easing};
        &:hover {
            color: ${(p) => p.theme.palette.accent[p.accentColor]};
        }
    }
    svg.arrow {
        cursor: pointer;
        font-size: 56px;
        position: relative;
        transition: color 0.2s ${(p) => p.theme.easing};
        &:hover {
            color: ${(p) => p.theme.palette.accent[p.accentColor]};
        }
    }
`;

export interface ImageGallerySlideProps {
    image?: ImageProps;
    caption?: WithCaptionProps;
    eyebrow?: EyebrowProps;
    title?: string;
    logo?: ImageGalleryLogoProps;
    href?: string;
}

export interface ImageGalleryProps {
    title?: string;
    slides?: ImageGallerySlideProps[];
    accentColor?: AccentColorOptions;
    showScrollIndicator?: boolean;
}

const getPrevSlide = (
    slides: ImageGallerySlideProps[],
    activeSlideIndex: number
): ImageGallerySlideProps | null => {
    const onlyTwoAndAtEnd = activeSlideIndex === 1 && slides.length === 2;
    if (onlyTwoAndAtEnd) {
        return slides[0];
    }

    let newIndex = activeSlideIndex - 1;
    if (slides.length < 3) return null;
    if (slides && activeSlideIndex === 0) {
        newIndex = slides.length - 1;
    }
    return slides[newIndex];
};

const getNextSlide = (
    slides: ImageGallerySlideProps[],
    activeSlideIndex: number
): ImageGallerySlideProps | null => {
    if (slides.length < 2) return null;
    let newIndex = activeSlideIndex + 1;
    const onlyTwoAndAtEnd = newIndex === 2 && slides.length === 2;
    if (onlyTwoAndAtEnd) return null;
    if (slides && newIndex > slides.length - 1) {
        newIndex = 0;
    }
    return slides[newIndex];
};

export const ImageGallery = ({
    slides,
    accentColor = 'sky'
}: ImageGalleryProps): JSX.Element | null => {
    const [activeSlideIndex, setActiveSlideIndex] = useState(0);
    const prev = useCallback(() => {
        let newIndex = activeSlideIndex - 1;
        if (slides && activeSlideIndex === 0) {
            newIndex = slides.length - 1;
        }
        setActiveSlideIndex(newIndex);
    }, [setActiveSlideIndex, activeSlideIndex, slides]);

    const next = useCallback(() => {
        let newIndex = activeSlideIndex + 1;
        if (slides && newIndex > slides.length - 1) {
            newIndex = 0;
        }
        setActiveSlideIndex(newIndex);
    }, [setActiveSlideIndex, activeSlideIndex, slides]);

    const isSm = useBreakpointDown('sm');
    const {carousel} = useUnrealUIContext();
    const swipeHandlers = useSwipeable({
        onSwipedLeft: () => {
            next();
        },
        onSwipedRight: () => {
            prev();
        }
    });

    if (!slides || !slides.length) return null;

    const singleSlide = slides.length === 1;
    const activeSlide = slides[activeSlideIndex];
    const prevSlide = getPrevSlide(slides, activeSlideIndex);
    const nextSlide = getNextSlide(slides, activeSlideIndex);

    return (
        <Styled accentColor={accentColor}>
            <GridContainer>
                <VerticalSpacer variant="custom" spacing={6} downSmSpacing={5} />
                <CenteredGrid>
                    {!isSm && !singleSlide && (
                        <Grid item xs={false} md={1} lg={2}>
                            <div className="peak-slide-wrap">
                                <div className="arrow-wrap">
                                    {prevSlide &&
                                        (carousel?.icon === 'alt' ? (
                                            <CarouselArrowLeftAlt onClick={prev} />
                                        ) : (
                                            <ArrowLeft onClick={prev} />
                                        ))}
                                </div>
                                <ImageGalleryPeak image={prevSlide?.image} />
                            </div>
                        </Grid>
                    )}
                    {!isSm && singleSlide && <Grid item xs={false} md={1} />}
                    <Grid item xs={12} md={10} lg={singleSlide ? 10 : 8}>
                        <div {...swipeHandlers} className="active-slide-wrap">
                            <ImageGalleryLogo {...activeSlide.logo} xl={singleSlide} />
                            <AnimatePresence>
                                <motion.div
                                    className="home-carousel-status-motion"
                                    key={activeSlide?.image?.src}
                                    initial={{translateY: 30, opacity: 0}}
                                    animate={{translateY: 0, opacity: 1}}
                                    transition={{duration: 0.4}}>
                                    <UeLink href={activeSlide.href}>
                                        <WithCaption {...activeSlide?.caption}>
                                            <Image
                                                {...activeSlide.image}
                                                key={activeSlide?.image?.src}
                                                fit="cover"
                                                ratio="rect"
                                                optMaxWidth={1000}
                                            />
                                        </WithCaption>
                                    </UeLink>
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </Grid>
                    {!isSm && !singleSlide && (
                        <Grid item xs={false} md={1} lg={2}>
                            <div className="peak-slide-wrap">
                                <div className="arrow-wrap">
                                    {nextSlide &&
                                        (carousel?.icon === 'alt' ? (
                                            <CarouselArrowRightAlt onClick={next} />
                                        ) : (
                                            <ArrowRight onClick={next} />
                                        ))}
                                </div>
                                <ImageGalleryPeak right image={nextSlide?.image} />
                            </div>
                        </Grid>
                    )}
                </CenteredGrid>
                <CenteredGrid>
                    {!isSm && <Grid item xs={false} md={1} lg={2} />}
                    <Grid item xs={12} md={10} lg={8} style={{paddingTop: 0, paddingBottom: 0}}>
                        {!singleSlide && <VerticalSpacer variant="heading" downSmSpacing={2} />}
                        <div className="track-wrap">
                            {isSm && (
                                <div className={`arrow-wrap`}>
                                    {slides.length > 1 &&
                                        (carousel?.icon === 'alt' ? (
                                            <CarouselArrowLeftAlt onClick={prev} />
                                        ) : (
                                            <ArrowLeft onClick={prev} />
                                        ))}
                                </div>
                            )}
                            <ImageGalleryTrack
                                activeSlideIndex={activeSlideIndex}
                                setActiveSlideIndex={setActiveSlideIndex}
                                slidesLength={slides.length}
                            />
                            {isSm && (
                                <div className={`arrow-wrap`}>
                                    {slides.length > 1 &&
                                        (carousel?.icon === 'alt' ? (
                                            <CarouselArrowRightAlt onClick={next} />
                                        ) : (
                                            <ArrowRight onClick={next} />
                                        ))}
                                </div>
                            )}
                        </div>
                        <VerticalSpacer variant="heading" downSmSpacing={2} />
                        {activeSlide.eyebrow && activeSlide.eyebrow.text ? (
                            <>
                                <Eyebrow {...activeSlide.eyebrow} as="span" />
                                <VerticalSpacer variant="custom" spacing={1} />
                            </>
                        ) : (
                            ''
                        )}

                        <UeLink href={activeSlide.href}>
                            <Typography variant="h2">{activeSlide.title}</Typography>
                        </UeLink>
                        <VerticalSpacer variant="custom" spacing={2} />
                    </Grid>
                </CenteredGrid>
            </GridContainer>
        </Styled>
    );
};
